import React from 'react'
import { useForm, UseFormReturn } from 'react-hook-form'

import { PhoneInput, TextInput, WidgetBody } from '../../../base'
import { inputProperties } from '../../../types/inputProperties'
import { validators } from '../../../utils/validators'
import InputGroup from '../../../base/InputGroup/InputGroup'
import WidgetSection from '../../../base/Widget/WidgetSection'

/**
 * @interface PayerInformationProps
 * @param {UseFormReturn} form (Optional) - hook for managing forms with ease
 * @param {string} title (Optional) - title of the form
 */
export interface PayerInformationProps {
  form?: UseFormReturn
  onSubmit?: Function
  title: string
  firstName?: inputProperties
  lastName?: inputProperties
  businessName?: inputProperties
  phoneNumber?: inputProperties
  email?: inputProperties
}

/**
 * @param {PayerInformationProps} props
 * @param {UseFormReturn} props.form (Optional) - hook for managing forms with ease
 * @param {string} props.title (Optional) - title of the form
 */
const PayerInformation = (props: PayerInformationProps) => {
  const useFormDefault = props.form || useForm()

  const {
    formState: { isSubmitting },
  } = useFormDefault

  const onClickButton = () => {
    props.onSubmit && props.onSubmit()
  }

  Object.assign(PayerInformation.defaultProps.firstName, props.firstName)
  Object.assign(PayerInformation.defaultProps.lastName, props.lastName)
  Object.assign(PayerInformation.defaultProps.businessName, props.businessName)
  Object.assign(PayerInformation.defaultProps.phoneNumber, props.phoneNumber)
  Object.assign(PayerInformation.defaultProps.email, props.email)

  return (
    <WidgetBody
      isSubmitting={isSubmitting}
      title={props.title}
      onContinue={onClickButton}
    >
      <WidgetSection title='Full Name'>
        <InputGroup
          inputRows={[
            [
              <TextInput
                form={useFormDefault}
                rules={{ validate: validators.name('First Name') }}
                {...PayerInformation.defaultProps.firstName}
              />,
            ],
            [
              <TextInput
                form={useFormDefault}
                rules={{ validate: validators.name('Last Name') }}
                {...PayerInformation.defaultProps.lastName}
              />,
            ],
          ]}
        />
      </WidgetSection>
      <WidgetSection title='Business Name (Optional)'>
        <TextInput
          form={useFormDefault}
          rules={{ validate: validators.name('Business Name') }}
          {...PayerInformation.defaultProps.businessName}
        />
      </WidgetSection>
      <WidgetSection title='Contact Information'>
        <InputGroup
          inputRows={[
            [
              <PhoneInput
                form={useFormDefault}
                rules={{ validate: validators.phoneFormat }}
                {...PayerInformation.defaultProps.phoneNumber}
              />,
            ],
            [
              <TextInput
                form={useFormDefault}
                rules={{ validate: validators.emailFormat }}
                {...PayerInformation.defaultProps.email}
              />,
            ],
          ]}
        />
      </WidgetSection>
    </WidgetBody>
  )
}

PayerInformation.defaultProps = {
  email: {
    label: 'Email Address',
    name: 'email',
    required: true,
  },
  firstName: {
    label: 'First Name',
    name: 'firstName',
    required: true,
  },
  lastName: {
    label: 'Last Name',
    name: 'lastName',
    required: true,
  },
  businessName: {
    label: 'Business Name (Optional)',
    name: 'businessName',
  },
  phoneNumber: {
    label: 'Phone Number',
    name: 'phoneNumber',
    required: true,
  },
  title: 'Payer Information',
}

export default PayerInformation
